import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  BadgeCheck,
  CreditCard,
  Loader2,
  MonitorPlay,
  PackageCheck,
  ScanBarcode,
  UserPlus,
  Users2,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import { useLocaleFormatter } from "@/lib/i18n/useLocaleFormatter";
import {
  createRetailTransactionRecord,
  fetchRetailAssociates,
  fetchRetailCatalog,
  fetchRetailCustomers,
  fetchRetailRegisters,
  fetchRetailTransactions,
} from "@/lib/api/retail";
import type {
  RetailAssociate,
  RetailCustomerSuggestion,
  RetailProduct,
  RetailProductVariant,
  RetailRegister,
  RetailTransaction,
  RetailTaxPolicy,
} from "@/features/retail/types/datasets";
import RetailQuickSaleDialog from "@/features/retail/components/RetailQuickSaleDialog";

type PickupRow = {
  id: string;
  receiptNumber: string;
  createdAt: string;
  totalCents: number;
  status: RetailTransaction["status"];
};

type TaxPolicy = RetailTaxPolicy;

type CartItem = {
  lineId: string;
  kind: "catalog" | "service";
  productId: string | null;
  variantId: string | null;
  productName: string;
  variantName: string | null;
  unitPriceCents: number;
  quantity: number;
  taxPolicy: TaxPolicy;
  taxRateOverride: number | null;
};

const DEFAULT_OVERRIDE_RATE = 0.0825;

const RetailCheckoutPage = () => {
  const { t } = useTranslation(["common", "retail"]);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { formatCurrency: formatCurrencyLocale, formatRelativeTime, language } = useLocaleFormatter();

  const timeFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(language, {
        hour: "numeric",
        minute: "2-digit",
      }),
    [language],
  );

  const formatTime = useCallback(
    (value: string | null | undefined) => {
      if (!value) {
        return "--";
      }
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return "--";
      }
      return timeFormatter.format(date);
    },
    [timeFormatter],
  );

  const formatCurrencyFromCents = useCallback(
    (value: number, currency = "USD") => formatCurrencyLocale(value, currency, { isMinorUnit: true }),
    [formatCurrencyLocale],
  );

  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [selectedRegisterId, setSelectedRegisterId] = useState<string | null>(null);
  const [selectedAssociateId, setSelectedAssociateId] = useState<string | null>(null);
  const [tenderMethod, setTenderMethod] = useState<"cash" | "card" | "gift" | "store_credit">("card");
  const [loyaltyEmail, setLoyaltyEmail] = useState("");
  const [quickSaleOpen, setQuickSaleOpen] = useState(false);
  const [quickSaleMode, setQuickSaleMode] = useState<"standard" | "scan">("standard");
  const [barcodeValue, setBarcodeValue] = useState("");
  const [customerLookupOpen, setCustomerLookupOpen] = useState(false);
  const [customerSearch, setCustomerSearch] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState<RetailCustomerSuggestion | null>(null);
  const [serviceDialogOpen, setServiceDialogOpen] = useState(false);
  const [serviceName, setServiceName] = useState("");
  const [servicePrice, setServicePrice] = useState("25.00");
  const [serviceQuantity, setServiceQuantity] = useState("1");
  const [serviceTaxPolicy, setServiceTaxPolicy] = useState<TaxPolicy>("default");
  const [serviceTaxRate, setServiceTaxRate] = useState((DEFAULT_OVERRIDE_RATE * 100).toFixed(2));
  const serviceLineCounter = useRef(0);

  const resetServiceForm = useCallback(() => {
    setServiceName("");
    setServicePrice("25.00");
    setServiceQuantity("1");
    setServiceTaxPolicy("default");
    setServiceTaxRate((DEFAULT_OVERRIDE_RATE * 100).toFixed(2));
  }, []);

  const handleServiceDialogOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        resetServiceForm();
      }
      setServiceDialogOpen(open);
      if (!open) {
        resetServiceForm();
      }
    },
    [resetServiceForm],
  );

  const catalogQuery = useQuery({
    queryKey: ["retail", "catalog"],
    queryFn: ({ signal }) => fetchRetailCatalog({ signal }),
  });

  const transactionsQuery = useQuery({
    queryKey: ["retail", "transactions"],
    queryFn: ({ signal }) => fetchRetailTransactions({ signal }),
  });
  const associatesQuery = useQuery({
    queryKey: ["retail", "associates"],
    queryFn: ({ signal }) => fetchRetailAssociates({ signal }),
  });
  const registersQuery = useQuery({
    queryKey: ["retail", "registers"],
    queryFn: ({ signal }) => fetchRetailRegisters({ signal }),
  });

  const isOverviewLoading =
    transactionsQuery.isLoading || associatesQuery.isLoading || registersQuery.isLoading;
  const isError =
    transactionsQuery.isError ||
    associatesQuery.isError ||
    registersQuery.isError ||
    catalogQuery.isError;

  const pickupQueue = useMemo<PickupRow[]>(() => {
    const transactions = transactionsQuery.data ?? [];

    return transactions
      .filter((txn) => txn.channel === "bopis")
      .sort((left, right) => new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime())
      .map((txn) => ({
        id: txn.id,
        receiptNumber: txn.receiptNumber,
        createdAt: txn.createdAt,
        totalCents: txn.totalCents,
        status: txn.status,
      }));
  }, [transactionsQuery.data]);

  const activeRegisters = useMemo<RetailRegister[]>(() => {
    const registers = registersQuery.data ?? [];
    return registers.filter((register) => register.status !== "maintenance");
  }, [registersQuery.data]);

  const activeAssociates = useMemo<RetailAssociate[]>(() => {
    const associates = associatesQuery.data ?? [];
    return associates.filter((associate) => associate.isActive);
  }, [associatesQuery.data]);

  const catalogItems = useMemo(
    () => catalogQuery.data ?? [],
    [catalogQuery.data],
  );
  const topProducts = useMemo(() => catalogItems.slice(0, 6), [catalogItems]);
  const quickKeyProducts = useMemo(() => topProducts.slice(0, 4), [topProducts]);

  const customerSearchTerm = customerSearch.trim();
  const customersQuery = useQuery({
    queryKey: ["retail", "customers", customerSearchTerm],
    queryFn: ({ signal }) =>
      fetchRetailCustomers({
        search: customerSearchTerm.length > 0 ? customerSearchTerm : undefined,
        limit: 10,
        signal,
      }),
    staleTime: 30 * 1000,
  });
  const customerResults = customersQuery.data ?? [];

  useEffect(() => {
    if (activeRegisters.length > 0 && !selectedRegisterId) {
      setSelectedRegisterId(activeRegisters[0].id);
    }
  }, [activeRegisters, selectedRegisterId]);

  useEffect(() => {
    if (activeAssociates.length > 0 && !selectedAssociateId) {
      setSelectedAssociateId(activeAssociates[0].id);
    }
  }, [activeAssociates, selectedAssociateId]);

  useEffect(() => {
    if (!customerLookupOpen) {
      setCustomerSearch("");
    }
  }, [customerLookupOpen]);

  const selectedRegister = useMemo(
    () => activeRegisters.find((register) => register.id === selectedRegisterId) ?? null,
    [activeRegisters, selectedRegisterId],
  );

  const cartSubtotal = useMemo(
    () => cartItems.reduce((total, item) => total + item.quantity * item.unitPriceCents, 0),
    [cartItems],
  );

  const serviceLineCount = useMemo(
    () => cartItems.filter((item) => item.kind === "service").length,
    [cartItems],
  );

  const overrideLineCount = useMemo(
    () => cartItems.filter((item) => item.taxPolicy === "override").length,
    [cartItems],
  );

  const exemptLineCount = useMemo(
    () => cartItems.filter((item) => item.taxPolicy === "exempt").length,
    [cartItems],
  );

  const resolveLineTaxRate = useCallback((item: CartItem) => {
    if (item.taxPolicy === "exempt") {
      return 0;
    }
    if (item.taxPolicy === "override" && typeof item.taxRateOverride === "number") {
      return item.taxRateOverride;
    }
    return DEFAULT_OVERRIDE_RATE;
  }, []);

  const cartTaxCents = useMemo(
    () =>
      cartItems.reduce((total, item) => {
        const rate = resolveLineTaxRate(item);
        const lineSubtotal = item.quantity * item.unitPriceCents;
        return total + Math.round(lineSubtotal * rate);
      }, 0),
    [cartItems, resolveLineTaxRate],
  );

  const cartTotalCents = cartSubtotal + cartTaxCents;

  const createTransactionMutation = useMutation({
    mutationFn: createRetailTransactionRecord,
    onSuccess: (transaction) => {
      toast({
        title: t("retail.checkout.toast.successTitle", { defaultValue: "Sale recorded" }),
        description: t("retail.checkout.toast.successDescription", {
          receipt: transaction.receiptNumber,
          defaultValue: `Receipt ${transaction.receiptNumber}`,
        }),
      });
      setCartItems([]);
      setLoyaltyEmail("");
      setSelectedCustomer(null);
      setBarcodeValue("");
      resetServiceForm();
      queryClient.invalidateQueries({ queryKey: ["retail", "transactions"] });
      queryClient.invalidateQueries({ queryKey: ["retail", "associates"] });
      queryClient.invalidateQueries({ queryKey: ["retail", "registers"] });
      queryClient.invalidateQueries({ queryKey: ["retail", "checkout"] });
    },
    onError: (error: unknown) => {
      toast({
        title: t("status.error", { defaultValue: "Something went wrong" }),
        description:
          error instanceof Error
            ? error.message
            : t("status.tryAgain", { defaultValue: "Please try again." }),
        variant: "destructive",
      });
    },
  });

  const buildCatalogLineId = (productId: string, variantId: string | null) =>
    `catalog-${productId}-${variantId ?? "base"}`;

  const nextServiceLineId = () => {
    serviceLineCounter.current += 1;
    return `service-${Date.now()}-${serviceLineCounter.current}`;
  };

  const findCatalogEntryByBarcode = useCallback(
    (barcode: string) => {
      const normalized = barcode.trim();
      if (!normalized) {
        return null;
      }
      for (const product of catalogItems) {
        if (product.barcode && product.barcode === normalized) {
          return { product, variant: null };
        }
        for (const variant of product.variants) {
          if (variant.barcode && variant.barcode === normalized) {
            return { product, variant };
          }
        }
      }
      return null;
    },
    [catalogItems],
  );

  const handleAddCatalogItem = (product: RetailProduct, variant: RetailProductVariant | null = null) => {
    const lineId = buildCatalogLineId(product.id, variant?.id ?? null);
    setCartItems((current) => {
      const existing = current.find((item) => item.lineId === lineId);
      if (existing) {
        return current.map((item) =>
          item.lineId === lineId ? { ...item, quantity: item.quantity + 1 } : item,
        );
      }
      return [
        ...current,
        {
          lineId,
          kind: "catalog",
          productId: product.id,
          variantId: variant?.id ?? null,
          productName: product.name,
          variantName: variant?.name ?? null,
          unitPriceCents: variant?.priceCents ?? product.defaultPriceCents,
          quantity: 1,
          taxPolicy: "default",
          taxRateOverride: null,
        },
      ];
    });
  };

  const handleQuantityChange = (lineId: string, delta: number) => {
    setCartItems((current) =>
      current
        .map((item) =>
          item.lineId === lineId
            ? { ...item, quantity: Math.max(0, item.quantity + delta) }
            : item,
        )
        .filter((item) => item.quantity > 0),
    );
  };

  const handleRemoveItem = (lineId: string) => {
    setCartItems((current) =>
      current.filter((item) => item.lineId !== lineId),
    );
  };

  const handleTaxPolicyChange = (lineId: string, policy: TaxPolicy) => {
    setCartItems((current) =>
      current.map((item) =>
        item.lineId === lineId
          ? {
              ...item,
              taxPolicy: policy,
              taxRateOverride: policy === "override" ? item.taxRateOverride ?? DEFAULT_OVERRIDE_RATE : null,
            }
          : item,
      ),
    );
  };

  const handleTaxRateOverrideBlur = (lineId: string, rawValue: string) => {
    const trimmed = rawValue.trim();
    if (trimmed.length === 0) {
      setCartItems((current) =>
        current.map((item) =>
          item.lineId === lineId
            ? {
                ...item,
                taxRateOverride: null,
              }
            : item,
        ),
      );
      return;
    }

    const numeric = Number.parseFloat(trimmed);
    if (!Number.isFinite(numeric) || numeric < 0 || numeric > 25) {
      toast({
        title: t("retail.checkout.tax.invalidOverride", { defaultValue: "Enter a valid tax percentage" }),
        description: t("retail.checkout.tax.invalidOverrideDescription", {
          defaultValue: "Use a value between 0 and 25%.",
        }),
        variant: "destructive",
      });
      return;
    }

    const rateDecimal = Math.round((numeric / 100) * 10000) / 10000;
    setCartItems((current) =>
      current.map((item) =>
        item.lineId === lineId
          ? {
              ...item,
              taxRateOverride: rateDecimal,
            }
          : item,
      ),
    );
  };

  const handleServiceSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedName = serviceName.trim();
    if (trimmedName.length === 0) {
      toast({
        title: t("retail.checkout.counter.serviceNameError", { defaultValue: "Service name required" }),
        description: t("retail.checkout.counter.serviceNameHint", {
          defaultValue: "Enter a name so the receipt shows the service line.",
        }),
        variant: "destructive",
      });
      return;
    }

    const priceValue = Number.parseFloat(servicePrice);
    if (!Number.isFinite(priceValue) || priceValue <= 0) {
      toast({
        title: t("retail.checkout.counter.servicePriceError", { defaultValue: "Enter a valid price" }),
        description: t("retail.checkout.counter.servicePriceHint", {
          defaultValue: "Use a positive amount like 45 or 45.00.",
        }),
        variant: "destructive",
      });
      return;
    }

    const quantityValue = Math.max(1, Math.round(Number.parseFloat(serviceQuantity) || 1));

    let taxOverrideDecimal: number | null = null;
    if (serviceTaxPolicy === "override") {
      const overrideValue = Number.parseFloat(serviceTaxRate);
      if (!Number.isFinite(overrideValue) || overrideValue < 0 || overrideValue > 25) {
        toast({
          title: t("retail.checkout.counter.serviceTaxError", { defaultValue: "Override must be between 0 and 25%" }),
          description: t("retail.checkout.counter.serviceTaxHint", {
            defaultValue: "Adjust the percentage or choose tax exempt/default.",
          }),
          variant: "destructive",
        });
        return;
      }
      taxOverrideDecimal = Math.round((overrideValue / 100) * 10000) / 10000;
    }

    const lineId = nextServiceLineId();
    const unitPriceCents = Math.round(priceValue * 100);

    setCartItems((current) => [
      ...current,
      {
        lineId,
        kind: "service",
        productId: null,
        variantId: null,
        productName: trimmedName,
        variantName: null,
        unitPriceCents,
        quantity: quantityValue,
        taxPolicy: serviceTaxPolicy,
        taxRateOverride: serviceTaxPolicy === "override" ? taxOverrideDecimal : null,
      },
    ]);

    handleServiceDialogOpenChange(false);
  };

  const handleBarcodeSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!barcodeValue.trim()) {
      toast({
        title: t("retail.checkout.counter.barcodeError", { defaultValue: "Barcode not recognised" }),
        description: t("retail.checkout.counter.barcodeHint", {
          defaultValue: "Scan a valid SKU or use quick keys instead.",
        }),
        variant: "destructive",
      });
      return;
    }

    const match = findCatalogEntryByBarcode(barcodeValue);
    if (!match) {
      toast({
        title: t("retail.checkout.counter.barcodeError", { defaultValue: "Barcode not recognised" }),
        description: t("retail.checkout.counter.barcodeHint", {
          defaultValue: "Scan a valid SKU or use quick keys instead.",
        }),
        variant: "destructive",
      });
      return;
    }

    handleAddCatalogItem(match.product, match.variant);
    setBarcodeValue("");
    toast({
      title: t("retail.checkout.counter.barcodeSuccess", { defaultValue: "Item added" }),
      description: match.variant
        ? `${match.product.name} · ${match.variant.name}`
        : match.product.name,
    });
  };

  const handleSelectCustomer = (customer: RetailCustomerSuggestion) => {
    setSelectedCustomer(customer);
    setLoyaltyEmail(customer.email ?? "");
    setCustomerLookupOpen(false);
    toast({
      title: t("retail.checkout.customers.linkedTitle", { defaultValue: "Customer linked" }),
      description: t("retail.checkout.customers.linkedDescription", {
        defaultValue: "{{name}} will receive the digital receipt.",
        name: customer.name ?? (customer.email ?? ""),
      }),
    });
  };

  const handleClearCustomer = () => {
    setSelectedCustomer(null);
    setLoyaltyEmail("");
  };

  const handleCheckout = () => {
    if (cartItems.length === 0 || createTransactionMutation.isPending) {
      return;
    }

    const lineMetadata = cartItems
      .filter(
        (item) => item.kind === "service" || item.taxPolicy !== "default" || item.taxRateOverride !== null,
      )
      .map((item) => ({
        lineId: item.lineId,
        kind: item.kind,
        productId: item.productId,
        variantId: item.variantId,
        name: item.productName,
        quantity: item.quantity,
        taxPolicy: item.taxPolicy,
        taxRateOverride: item.taxRateOverride,
      }));

    const trimmedLoyaltyEmail = loyaltyEmail.trim();
    const resolvedCustomerProfileId = selectedCustomer?.id
      ? selectedCustomer.id.startsWith("profile:")
        ? selectedCustomer.id.slice("profile:".length)
        : selectedCustomer.id
      : null;
    const metadataBase: Record<string, unknown> = {};
    if (lineMetadata.length > 0) {
      metadataBase.lineItems = lineMetadata;
    }
    if (resolvedCustomerProfileId) {
      metadataBase.customerId = resolvedCustomerProfileId;
    }
    if (selectedCustomer?.name) {
      metadataBase.customerName = selectedCustomer.name;
    }
    if (selectedCustomer?.loyaltyId) {
      metadataBase.loyaltyId = selectedCustomer.loyaltyId;
    }
    if (trimmedLoyaltyEmail) {
      metadataBase.loyaltyEmail = trimmedLoyaltyEmail;
    }
    if (cartTaxCents > 0) {
      metadataBase.taxCents = cartTaxCents;
    }
    metadataBase.subtotalCents = cartSubtotal;
    metadataBase.defaultTaxRate = DEFAULT_OVERRIDE_RATE;

    const metadata = Object.keys(metadataBase).length > 0 ? metadataBase : undefined;

    createTransactionMutation.mutate({
      locationId: selectedRegister?.locationId ?? null,
      registerId: selectedRegisterId,
      associateId: selectedAssociateId,
      channel: "pos",
      status: "completed",
      currency: "USD",
      metadata,
      items: cartItems.map((item) => ({
        productId: item.productId,
        variantId: item.variantId,
        quantity: item.quantity,
        unitPriceCents: item.unitPriceCents,
        discountCents: 0,
        kind: item.kind,
        taxPolicy: item.taxPolicy,
        taxRateOverride:
          item.taxPolicy === "override"
            ? item.taxRateOverride ?? DEFAULT_OVERRIDE_RATE
            : item.taxPolicy === "default"
            ? undefined
            : null,
      })),
      tenders: [
        {
          method: tenderMethod,
          amountCents: cartTotalCents,
        },
      ],
      customerProfileId: resolvedCustomerProfileId,
      loyaltyEmail: trimmedLoyaltyEmail.length > 0 ? trimmedLoyaltyEmail : null,
      customerName:
        selectedCustomer?.name ??
        selectedCustomer?.email ??
        (trimmedLoyaltyEmail ? trimmedLoyaltyEmail.split("@")[0] : null),
    });
  };

  const isCheckoutDisabled = cartItems.length === 0 || createTransactionMutation.isPending;

  const readyPickupCount = useMemo(
    () => pickupQueue.filter((pickup) => pickup.status === "completed").length,
    [pickupQueue],
  );

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <Badge className="border border-emerald-500/25 bg-emerald-500/10 text-emerald-600 dark:border-emerald-500/20 dark:bg-emerald-500/15 dark:text-emerald-200">
            {t("retail.checkout.previewBadge", { defaultValue: "Retail preview" })}
          </Badge>
          <h1 className="mt-2 text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">
            {t("retail.checkout.title", { defaultValue: "Checkout workbench" })}
          </h1>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            {t("retail.checkout.subtitle", {
              defaultValue:
                "Manage counter sales, pickups, and ship-from-store orders from a single command center.",
            })}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap">
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={() => {
              setQuickSaleMode("standard");
              setQuickSaleOpen(true);
            }}
          >
            <MonitorPlay className="h-4 w-4" />
            {t("retail.checkout.actions.launchCounter", { defaultValue: "Launch counter sale" })}
          </Button>
          <Button
            size="sm"
            className="gap-2"
            onClick={() => {
              setQuickSaleMode("scan");
              setQuickSaleOpen(true);
            }}
          >
            <ScanBarcode className="h-4 w-4" />
            {t("retail.checkout.actions.startScan", { defaultValue: "Start quick scan" })}
          </Button>
        </div>
      </header>

      {isError ? (
        <Alert variant="destructive">
          <AlertTitle>{t("retail.checkout.errors.title", { defaultValue: "Checkout data unavailable" })}</AlertTitle>
          <AlertDescription>
            {t("retail.checkout.errors.description", {
              defaultValue: "We could not load the current register and pickup state. Try refreshing to retry.",
            })}
          </AlertDescription>
        </Alert>
      ) : null}

      <section className="grid gap-4 xl:grid-cols-[minmax(0,0.65fr)_minmax(0,0.35fr)]">
        <div className="space-y-4">
          <Card className="border border-slate-200/70 bg-white/92 shadow-sm backdrop-blur dark:border-slate-800/60 dark:bg-slate-950/55">
            <CardHeader className="flex flex-col gap-1">
              <CardTitle className="text-lg text-slate-900 dark:text-slate-100">
                {t("retail.checkout.counter.title", { defaultValue: "Counter lane controls" })}
              </CardTitle>
              <CardDescription className="text-slate-500 dark:text-slate-400">
                {t("retail.checkout.counter.subtitle", {
                  defaultValue: "Scan barcodes, tap quick keys, and link customers before tendering.",
                })}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <form className="space-y-2" onSubmit={handleBarcodeSubmit}>
                <span className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  {t("retail.checkout.counter.barcodeLabel", { defaultValue: "Scan or search" })}
                </span>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <div className="relative flex-1">
                    <ScanBarcode className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <Input
                      value={barcodeValue}
                      onChange={(event) => setBarcodeValue(event.target.value)}
                      placeholder={t("retail.checkout.counter.barcodePlaceholder", {
                        defaultValue: "Scan barcode or enter SKU",
                      })}
                      className="pl-9"
                      disabled={createTransactionMutation.isPending}
                    />
                  </div>
                  <Button type="submit" variant="secondary" className="shrink-0 gap-2" disabled={createTransactionMutation.isPending}>
                    <ScanBarcode className="h-4 w-4" />
                    {t("retail.checkout.counter.barcodeSubmit", { defaultValue: "Add item" })}
                  </Button>
                </div>
              </form>

              <div className="space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2 sm:flex-nowrap">
                  <span className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    {t("retail.checkout.counter.quickKeysLabel", { defaultValue: "Quick keys" })}
                  </span>
                  <span className="text-xs text-slate-400 dark:text-slate-500">
                    {t("retail.checkout.counter.quickKeysHint", { defaultValue: "Top sellers from today's catalog" })}
                  </span>
                </div>
                {quickKeyProducts.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-slate-200/70 px-4 py-6 text-center text-sm text-slate-500 dark:border-slate-800/70 dark:text-slate-400">
                    {t("retail.checkout.counter.quickKeysEmpty", { defaultValue: "Add products to feature quick keys." })}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                    {quickKeyProducts.map((product) => (
                      <Button
                        key={product.id}
                        variant="outline"
                        className="h-auto flex-col items-start gap-1 rounded-xl border-slate-200/70 px-3 py-2 text-left dark:border-slate-800/60"
                        onClick={() => handleAddCatalogItem(product)}
                        disabled={createTransactionMutation.isPending}
                      >
                        <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">{product.name}</span>
                        <span className="text-xs text-slate-500 dark:text-slate-400">
                          {formatCurrencyFromCents(product.defaultPriceCents)}
                        </span>
                      </Button>
                    ))}
                  </div>
                )}
              </div>

              <div className="rounded-xl border border-slate-200/70 bg-white/95 px-4 py-3 dark:border-slate-800/60 dark:bg-slate-950/60">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="space-y-1">
                    <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                      {t("retail.checkout.counter.serviceCardTitle", { defaultValue: "Add services on the fly" })}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {t("retail.checkout.counter.serviceCardSubtitle", {
                        defaultValue: "Capture fittings, personalization, or labor before closing the sale.",
                      })}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-2"
                    onClick={() => handleServiceDialogOpenChange(true)}
                  >
                    <PackageCheck className="h-4 w-4" />
                    {t("retail.checkout.counter.serviceButton", { defaultValue: "Add service" })}
                  </Button>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200/70 bg-white/95 px-4 py-3 dark:border-slate-800/60 dark:bg-slate-950/60">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="space-y-1">
                    <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                      {selectedCustomer
                        ? selectedCustomer.name
                        : t("retail.checkout.counter.customerTitle", { defaultValue: "Customer lookup" })}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {selectedCustomer
                        ? selectedCustomer.email
                        : t("retail.checkout.counter.customerSubtitle", {
                            defaultValue: "Link loyalty guests to capture receipts and rewards.",
                          })}
                    </p>
                    {selectedCustomer ? (
                      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                        <Badge variant="outline" className="uppercase tracking-wide">
                          {t("retail.checkout.customers.loyaltyId", {
                            defaultValue: "Loyalty {{id}}",
                            id: selectedCustomer.loyaltyId,
                          })}
                        </Badge>
                        <span>
                          {t("retail.checkout.customers.spendToDate", {
                            defaultValue: "Spend to date {{amount}}",
                            amount: formatCurrencyFromCents(selectedCustomer.spendToDateCents),
                          })}
                        </span>
                        <span>
                          {t("retail.checkout.customers.lastSeen", {
                            defaultValue: "Last visit {{time}}",
                            time: formatRelativeTime(selectedCustomer.lastVisit),
                          })}
                        </span>
                      </div>
                    ) : null}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {selectedCustomer ? (
                      <Button variant="ghost" size="sm" onClick={handleClearCustomer}>
                        {t("retail.checkout.counter.customerClear", { defaultValue: "Clear" })}
                      </Button>
                    ) : null}
                    <Button size="sm" variant="outline" className="gap-2" onClick={() => setCustomerLookupOpen(true)}>
                      <UserPlus className="h-4 w-4" />
                      {t("retail.checkout.counter.customerButton", { defaultValue: "Open lookup" })}
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border border-slate-200/70 bg-white/92 shadow-sm backdrop-blur dark:border-slate-800/60 dark:bg-slate-950/55">
            <CardHeader className="flex flex-col gap-1">
              <CardTitle className="text-lg text-slate-900 dark:text-slate-100">
                {t("retail.checkout.cart.title", { defaultValue: "Current cart" })}
              </CardTitle>
              <CardDescription className="text-slate-500 dark:text-slate-400">
                {t("retail.checkout.cart.subtitle", {
                  defaultValue: "Scan or tap quick add buttons to build a basket before tendering.",
                })}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {cartItems.length === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-200/70 px-4 py-8 text-center text-sm text-slate-500 dark:border-slate-800/70 dark:text-slate-400">
                  {t("retail.checkout.cart.empty", { defaultValue: "No items in the cart yet." })}
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="-mx-4 overflow-x-auto sm:mx-0">
                    <Table className="min-w-[640px] md:min-w-[700px]">
                      <TableHeader>
                        <TableRow className="border-slate-200/70 dark:border-slate-800/70">
                          <TableHead>{t("retail.checkout.cart.columns.item", { defaultValue: "Item" })}</TableHead>
                          <TableHead className="w-[140px] text-right">
                            {t("retail.checkout.cart.columns.quantity", { defaultValue: "Quantity" })}
                          </TableHead>
                          <TableHead className="w-[120px] text-right">
                            {t("retail.checkout.cart.columns.price", { defaultValue: "Price" })}
                          </TableHead>
                          <TableHead className="w-[120px] text-right">
                            {t("retail.checkout.cart.columns.total", { defaultValue: "Total" })}
                          </TableHead>
                          <TableHead className="w-[80px] text-right" />
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {cartItems.map((item) => (
                          <TableRow
                            key={item.variantId ? `${item.productId}:${item.variantId}` : item.productId}
                            className="border-slate-200/70 dark:border-slate-800/70"
                          >
                            <TableCell>
                              <div className="flex flex-col gap-1">
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                                    {item.productName}
                                  </span>
                                  {item.kind === "service" ? (
                                    <Badge
                                      variant="outline"
                                      className="rounded-full px-2 py-[1px] text-[10px] font-semibold uppercase tracking-wide"
                                    >
                                      {t("retail.checkout.cart.serviceBadge", { defaultValue: "Service" })}
                                    </Badge>
                                  ) : null}
                                </div>
                                {item.variantName ? (
                                  <span className="text-xs text-slate-500 dark:text-slate-400">{item.variantName}</span>
                                ) : null}
                                <div className="flex flex-wrap items-center gap-2 pt-1">
                                  <Select
                                    value={item.taxPolicy}
                                    onValueChange={(value) => handleTaxPolicyChange(item.lineId, value as TaxPolicy)}
                                  >
                                    <SelectTrigger className="h-7 w-[164px] text-xs">
                                      <SelectValue
                                        placeholder={t("retail.checkout.tax.default", { defaultValue: "Use location tax" })}
                                      />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="default">
                                        {t("retail.checkout.tax.default", { defaultValue: "Use location tax" })}
                                      </SelectItem>
                                      <SelectItem value="override">
                                        {t("retail.checkout.tax.override", { defaultValue: "Override rate" })}
                                      </SelectItem>
                                      <SelectItem value="exempt">
                                        {t("retail.checkout.tax.exempt", { defaultValue: "Tax exempt" })}
                                      </SelectItem>
                                    </SelectContent>
                                  </Select>
                                  {item.taxPolicy === "override" ? (
                                    <div className="flex items-center gap-1">
                                      <Input
                                        key={`${item.lineId}-${item.taxRateOverride ?? ""}`}
                                        defaultValue={
                                          item.taxRateOverride !== null
                                            ? (item.taxRateOverride * 100).toFixed(2)
                                            : ""
                                        }
                                        onBlur={(event) => handleTaxRateOverrideBlur(item.lineId, event.target.value)}
                                        inputMode="decimal"
                                        placeholder={t("retail.checkout.tax.overridePlaceholder", {
                                          defaultValue: "Rate %",
                                        })}
                                        className="h-7 w-24 text-xs"
                                      />
                                      <span className="text-xs text-slate-500 dark:text-slate-400">%</span>
                                    </div>
                                  ) : null}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  variant="outline"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={() => handleQuantityChange(item.lineId, -1)}
                                  disabled={createTransactionMutation.isPending}
                                >
                                  −
                                </Button>
                                <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                                  {item.quantity}
                                </span>
                                <Button
                                  variant="outline"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={() => handleQuantityChange(item.lineId, 1)}
                                  disabled={createTransactionMutation.isPending}
                                >
                                  +
                                </Button>
                              </div>
                            </TableCell>
                            <TableCell className="text-right text-sm text-slate-600 dark:text-slate-300">
                              {formatCurrencyFromCents(item.unitPriceCents)}
                            </TableCell>
                            <TableCell className="text-right text-sm font-semibold text-slate-900 dark:text-slate-100">
                              {formatCurrencyFromCents(item.unitPriceCents * item.quantity)}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-slate-500 hover:text-rose-500"
                                onClick={() => handleRemoveItem(item.lineId)}
                                disabled={createTransactionMutation.isPending}
                                aria-label={t("retail.checkout.cart.remove", { defaultValue: "Remove item" })}
                              >
                                ×
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="space-y-1">
                        <span className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                          {t("retail.checkout.cart.registerLabel", { defaultValue: "Register" })}
                        </span>
                        <Select
                          value={selectedRegisterId ?? ""}
                          onValueChange={(value) => setSelectedRegisterId(value)}
                          disabled={activeRegisters.length === 0 || createTransactionMutation.isPending}
                        >
                          <SelectTrigger>
                            <SelectValue
                              placeholder={t("retail.checkout.cart.registerPlaceholder", {
                                defaultValue: "Select register",
                              })}
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {activeRegisters.map((register) => (
                              <SelectItem key={register.id} value={register.id}>
                                {register.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <span className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                          {t("retail.checkout.cart.associateLabel", { defaultValue: "Associate" })}
                        </span>
                        <Select
                          value={selectedAssociateId ?? ""}
                          onValueChange={(value) => setSelectedAssociateId(value)}
                          disabled={activeAssociates.length === 0 || createTransactionMutation.isPending}
                        >
                          <SelectTrigger>
                            <SelectValue
                              placeholder={t("retail.checkout.cart.associatePlaceholder", {
                                defaultValue: "Select associate",
                              })}
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {activeAssociates.map((associate) => (
                              <SelectItem key={associate.id} value={associate.id}>
                                {associate.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <span className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                          {t("retail.checkout.cart.tenderLabel", { defaultValue: "Tender" })}
                        </span>
                        <Select
                          value={tenderMethod}
                          onValueChange={(value) => setTenderMethod(value as typeof tenderMethod)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="card">{t("retail.checkout.cart.tenders.card", { defaultValue: "Card" })}</SelectItem>
                            <SelectItem value="cash">{t("retail.checkout.cart.tenders.cash", { defaultValue: "Cash" })}</SelectItem>
                            <SelectItem value="gift">{t("retail.checkout.cart.tenders.gift", { defaultValue: "Gift" })}</SelectItem>
                            <SelectItem value="store_credit">{t("retail.checkout.cart.tenders.storeCredit", { defaultValue: "Store credit" })}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <span className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                          {selectedCustomer
                            ? t("retail.checkout.cart.loyaltyLabelLinked", {
                                defaultValue: "Digital receipt for {{name}}",
                                name: selectedCustomer.name,
                              })
                            : t("retail.checkout.cart.loyaltyLabel", { defaultValue: "Loyalty capture" })}
                        </span>
                        <Input
                          value={loyaltyEmail}
                          onChange={(event) => setLoyaltyEmail(event.target.value)}
                          placeholder={t("retail.checkout.cart.loyaltyPlaceholder", {
                            defaultValue: "Email or phone (optional)",
                          })}
                          disabled={createTransactionMutation.isPending}
                        />
                      </div>
                    </div>
                  <div className="flex flex-col items-end gap-2">
                    {serviceLineCount > 0 ? (
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {t("retail.checkout.cart.summaryServices", {
                          count: serviceLineCount,
                          defaultValue: "{{count}} service lines",
                        })}
                      </p>
                    ) : null}
                    {overrideLineCount > 0 ? (
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {t("retail.checkout.cart.summaryOverrides", {
                          count: overrideLineCount,
                          defaultValue: "{{count}} custom tax overrides",
                        })}
                      </p>
                    ) : null}
                    {exemptLineCount > 0 ? (
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {t("retail.checkout.cart.summaryExempt", {
                          count: exemptLineCount,
                          defaultValue: "{{count}} tax exempt lines",
                        })}
                      </p>
                    ) : null}
                    {cartTaxCents > 0 ? (
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {t("retail.checkout.cart.summaryTax", {
                          amount: formatCurrencyFromCents(cartTaxCents),
                          defaultValue: "Tax {{amount}}",
                        })}
                      </p>
                    ) : null}
                    <div className="text-sm text-slate-500 dark:text-slate-400">
                      {t("retail.checkout.cart.total", { defaultValue: "Total" })}
                      <span className="ml-2 text-lg font-semibold text-slate-900 dark:text-slate-100">
                        {formatCurrencyFromCents(cartTotalCents)}
                      </span>
                    </div>
                    <Button onClick={handleCheckout} disabled={isCheckoutDisabled} className="gap-2">
                        {createTransactionMutation.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <CreditCard className="h-4 w-4" />
                        )}
                        {t("retail.checkout.cart.completeSale", { defaultValue: "Complete sale" })}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4">
          <Card className="border border-slate-200/70 bg-white/92 shadow-sm backdrop-blur dark:border-slate-800/60 dark:bg-slate-950/55">
            <CardHeader className="flex flex-col gap-1">
              <CardTitle className="text-lg text-slate-900 dark:text-slate-100">
                {t("retail.checkout.quickAdd.title", { defaultValue: "Quick add" })}
              </CardTitle>
              <CardDescription className="text-slate-500 dark:text-slate-400">
              {t("retail.checkout.quickAdd.subtitle", {
                defaultValue: "Feature top sellers for rapid checkout.",
              })}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {catalogQuery.isLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 4 }).map((_, index) => (
                  <Skeleton key={`quick-add-skeleton-${index}`} className="h-12 rounded-xl bg-slate-200/60 dark:bg-slate-800/60" />
                ))}
              </div>
            ) : topProducts.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-200/70 px-4 py-6 text-center text-sm text-slate-500 dark:border-slate-800/70 dark:text-slate-400">
                {t("retail.checkout.quickAdd.empty", { defaultValue: "Add products to see quick actions." })}
              </div>
            ) : (
              <div className="space-y-2">
                {topProducts.map((product) => (
                  <div
                    key={product.id}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200/70 bg-white/95 px-4 py-3 dark:border-slate-800/60 dark:bg-slate-950/60 sm:flex-nowrap"
                  >
                    <div className="flex flex-col">
                      <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                        {product.name}
                      </span>
                      <span className="text-xs text-slate-500 dark:text-slate-400">
                        {formatCurrencyFromCents(product.defaultPriceCents)}
                      </span>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleAddCatalogItem(product)}
                      disabled={createTransactionMutation.isPending}
                    >
                      {t("retail.checkout.quickAdd.add", { defaultValue: "Add" })}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        </div>
      </section>

      <section className="grid gap-4 lg:grid-cols-[minmax(0,0.6fr)_minmax(0,1fr)]">
        <Card className="border border-slate-200/70 bg-white/92 shadow-sm backdrop-blur dark:border-slate-800/60 dark:bg-slate-950/55">
          <CardHeader className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center justify-between gap-2 sm:flex-nowrap">
              <div>
                <CardTitle className="text-lg text-slate-900 dark:text-slate-100">
                  {t("retail.checkout.registers.title", { defaultValue: "Active registers" })}
                </CardTitle>
                <CardDescription className="text-slate-500 dark:text-slate-400">
                  {t("retail.checkout.registers.subtitle", {
                    defaultValue: "Monitor online/offline terminals and plan coverage.",
                  })}
                </CardDescription>
              </div>
              <Badge variant="outline" className="gap-2 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-300">
                <CreditCard className="h-4 w-4 text-emerald-500 dark:text-emerald-300" />
                {t("retail.checkout.registers.badge", { defaultValue: "POS readiness" })}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {isOverviewLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 3 }).map((_, index) => (
                  <Skeleton key={`register-skeleton-${index}`} className="h-12 rounded-xl bg-slate-200/60 dark:bg-slate-800/60" />
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                {activeRegisters.map((register) => (
                  <div
                    key={register.id}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200/70 bg-white/95 px-4 py-3 transition hover:border-emerald-500/40 hover:bg-emerald-50/40 dark:border-slate-800/60 dark:bg-slate-950/60 dark:hover:border-emerald-500/40 dark:hover:bg-emerald-500/10 sm:flex-nowrap"
                  >
                    <div className="flex flex-col">
                      <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">{register.label}</span>
                      <span className="text-xs text-slate-500 dark:text-slate-400">
                        {register.status === "online"
                          ? t("retail.checkout.registers.status.online", { defaultValue: "Online" })
                          : register.status === "offline"
                          ? t("retail.checkout.registers.status.offline", { defaultValue: "Offline" })
                          : t("retail.checkout.registers.status.maintenance", { defaultValue: "Maintenance" })}
                      </span>
                    </div>
                    <Badge
                      className={
                        register.status === "online"
                          ? "border border-emerald-200/70 bg-emerald-100/60 text-emerald-700 dark:border-emerald-500/35 dark:bg-emerald-500/15 dark:text-emerald-200"
                          : "border border-amber-200/70 bg-amber-100/60 text-amber-700 dark:border-amber-400/35 dark:bg-amber-500/15 dark:text-amber-200"
                      }
                    >
                      {register.status === "online"
                        ? t("retail.checkout.registers.badges.ready", { defaultValue: "Ready" })
                        : t("retail.checkout.registers.badges.action", { defaultValue: "Action needed" })}
                    </Badge>
                  </div>
                ))}
                {activeRegisters.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-slate-200/70 px-4 py-6 text-center text-sm text-slate-500 dark:border-slate-800/70 dark:text-slate-400">
                    {t("retail.checkout.registers.empty", { defaultValue: "No registers are connected yet." })}
                  </div>
                ) : null}
              </div>
            )}
          </CardContent>
      </Card>

      <Card className="border border-slate-200/70 bg-white/92 shadow-sm backdrop-blur dark:border-slate-800/60 dark:bg-slate-950/55">
        <CardHeader className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
              <CardTitle className="text-lg text-slate-900 dark:text-slate-100">
                {t("retail.checkout.pickups.title", { defaultValue: "Pickup queue" })}
              </CardTitle>
              <CardDescription className="text-slate-500 dark:text-slate-400">
                {t("retail.checkout.pickups.subtitle", {
                  defaultValue: "Confirm BOPIS orders and alert guests when they are staged.",
                })}
              </CardDescription>
            </div>
            <Badge variant="outline" className="gap-2 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-300">
              <PackageCheck className="h-4 w-4 text-emerald-500 dark:text-emerald-300" />
              {t("retail.checkout.pickups.badge", {
                count: readyPickupCount,
                defaultValue: "{{count}} ready",
              })}
            </Badge>
          </CardHeader>
          <CardContent className="space-y-2">
            {isOverviewLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 4 }).map((_, index) => (
                  <Skeleton key={`pickup-skeleton-${index}`} className="h-14 rounded-xl bg-slate-200/60 dark:bg-slate-800/60" />
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                {pickupQueue.map((order) => (
                  <div
                    key={order.id}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200/70 bg-white/95 px-4 py-3 transition hover:border-emerald-500/40 hover:bg-emerald-50/40 dark:border-slate-800/60 dark:bg-slate-950/60 dark:hover:border-emerald-500/40 dark:hover:bg-emerald-500/10"
                  >
                    <div>
                      <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{order.receiptNumber}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {t("retail.checkout.pickups.placedAt", {
                          defaultValue: "Placed {{time}}",
                          time: formatTime(order.createdAt),
                        })}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 sm:flex-nowrap">
                      <Badge
                        variant={order.status === "completed" ? "default" : "secondary"}
                        className="rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wide"
                      >
                        {order.status === "completed"
                          ? t("retail.checkout.pickups.status.ready", { defaultValue: "Ready" })
                          : t("retail.checkout.pickups.status.pending", { defaultValue: "Pending" })}
                      </Badge>
                      <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                        {formatCurrencyFromCents(order.totalCents)}
                      </span>
                    </div>
                  </div>
                ))}
                {pickupQueue.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-slate-200/70 px-4 py-6 text-center text-sm text-slate-500 dark:border-slate-800/70 dark:text-slate-400">
                    {t("retail.checkout.pickups.empty", { defaultValue: "No pickup orders need attention right now." })}
                  </div>
                ) : null}
              </div>
            )}
          </CardContent>
        </Card>
      </section>

      <Card className="border border-slate-200/70 bg-white/92 shadow-sm backdrop-blur dark:border-slate-800/60 dark:bg-slate-950/55">
        <CardHeader className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle className="text-lg text-slate-900 dark:text-slate-100">
              {t("retail.checkout.associates.title", { defaultValue: "Associate roster" })}
            </CardTitle>
            <CardDescription className="text-slate-500 dark:text-slate-400">
              {t("retail.checkout.associates.subtitle", {
                defaultValue: "See who is on shift, what roles they cover, and which registers they own.",
              })}
            </CardDescription>
          </div>
          <Badge variant="outline" className="gap-2 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-300">
            <Users2 className="h-4 w-4 text-emerald-500 dark:text-emerald-300" />
            {t("retail.checkout.associates.badge", { defaultValue: "Floor coverage" })}
          </Badge>
        </CardHeader>
        <CardContent className="space-y-2">
          {isOverviewLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 4 }).map((_, index) => (
                <Skeleton key={`associate-skeleton-${index}`} className="h-12 rounded-xl bg-slate-200/60 dark:bg-slate-800/60" />
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {activeAssociates.map((associate) => (
                <div
                  key={associate.id}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200/70 bg-white/95 px-4 py-3 dark:border-slate-800/60 dark:bg-slate-950/60"
                >
                  <div className="flex flex-col">
                    <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">{associate.name}</span>
                    <span className="text-xs text-slate-500 dark:text-slate-400">
                      {associate.role === "manager"
                        ? t("retail.checkout.associates.roles.manager", { defaultValue: "Manager" })
                        : associate.role === "lead"
                        ? t("retail.checkout.associates.roles.lead", { defaultValue: "Lead" })
                        : t("retail.checkout.associates.roles.associate", { defaultValue: "Associate" })}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 sm:flex-nowrap">
                    <div className="text-xs text-slate-500 dark:text-slate-400">
                      {associate.shiftStartedAt && associate.shiftEndsAt
                        ? t("retail.checkout.associates.shiftWindow", {
                            defaultValue: "{{start}} – {{end}}",
                            start: formatTime(associate.shiftStartedAt),
                            end: formatTime(associate.shiftEndsAt),
                          })
                        : t("retail.checkout.associates.offShift", { defaultValue: "Off shift" })}
                    </div>
                    <Badge variant="secondary" className="gap-1">
                      <BadgeCheck className="h-3 w-3" />
                      {associate.registersAssigned.length > 0
                        ? associate.registersAssigned.join(", ")
                        : t("retail.checkout.associates.noRegister", { defaultValue: "Unassigned" })}
                    </Badge>
                  </div>
                </div>
              ))}
              {activeAssociates.length === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-200/70 px-4 py-6 text-center text-sm text-slate-500 dark:border-slate-800/70 dark:text-slate-400">
                  {t("retail.checkout.associates.empty", { defaultValue: "No active associates at this time." })}
                </div>
              ) : null}
            </div>
          )}
      </CardContent>
    </Card>

    <Dialog open={serviceDialogOpen} onOpenChange={handleServiceDialogOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("retail.checkout.counter.serviceDialogTitle", { defaultValue: "Add service line" })}</DialogTitle>
          <DialogDescription>
            {t("retail.checkout.counter.serviceDialogSubtitle", {
              defaultValue: "Document scheduled services, fittings, or labor with custom tax handling.",
            })}
          </DialogDescription>
        </DialogHeader>
        <form className="space-y-4" onSubmit={handleServiceSubmit}>
          <div className="space-y-2">
            <Label htmlFor="service-name">
              {t("retail.checkout.counter.serviceNameLabel", { defaultValue: "Service name" })}
            </Label>
            <Input
              id="service-name"
              value={serviceName}
              onChange={(event) => setServiceName(event.target.value)}
              placeholder={t("retail.checkout.counter.serviceNamePlaceholder", { defaultValue: "Tailoring / Setup" })}
              disabled={createTransactionMutation.isPending}
              autoFocus
            />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="service-price">
                {t("retail.checkout.counter.servicePriceLabel", { defaultValue: "Unit price" })}
              </Label>
              <Input
                id="service-price"
                value={servicePrice}
                onChange={(event) => setServicePrice(event.target.value)}
                inputMode="decimal"
                placeholder="45.00"
                disabled={createTransactionMutation.isPending}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="service-quantity">
                {t("retail.checkout.counter.serviceQuantityLabel", { defaultValue: "Quantity" })}
              </Label>
              <Input
                id="service-quantity"
                value={serviceQuantity}
                onChange={(event) => setServiceQuantity(event.target.value)}
                inputMode="numeric"
                placeholder="1"
                disabled={createTransactionMutation.isPending}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>{t("retail.checkout.counter.serviceTaxLabel", { defaultValue: "Tax" })}</Label>
            <Select
              value={serviceTaxPolicy}
              onValueChange={(value) => {
                setServiceTaxPolicy(value as TaxPolicy);
                if (value !== "override") {
                  setServiceTaxRate((DEFAULT_OVERRIDE_RATE * 100).toFixed(2));
                }
              }}
            >
              <SelectTrigger className="h-9">
                <SelectValue placeholder={t("retail.checkout.tax.default", { defaultValue: "Use location tax" })} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default">
                  {t("retail.checkout.tax.default", { defaultValue: "Use location tax" })}
                </SelectItem>
                <SelectItem value="override">
                  {t("retail.checkout.tax.override", { defaultValue: "Override rate" })}
                </SelectItem>
                <SelectItem value="exempt">
                  {t("retail.checkout.tax.exempt", { defaultValue: "Tax exempt" })}
                </SelectItem>
              </SelectContent>
            </Select>
            {serviceTaxPolicy === "override" ? (
              <div className="flex items-center gap-2">
                <Input
                  id="service-tax-rate"
                  value={serviceTaxRate}
                  onChange={(event) => setServiceTaxRate(event.target.value)}
                  inputMode="decimal"
                  placeholder={t("retail.checkout.tax.overridePlaceholder", { defaultValue: "Rate %" })}
                  disabled={createTransactionMutation.isPending}
                  className="w-28"
                />
                <span className="text-sm text-slate-500 dark:text-slate-400">%</span>
              </div>
            ) : null}
          </div>
          <DialogFooter className="flex items-center gap-2">
            <Button type="button" variant="ghost" onClick={() => handleServiceDialogOpenChange(false)}>
              {t("retail.checkout.counter.serviceCancel", { defaultValue: "Cancel" })}
            </Button>
            <Button type="submit" disabled={createTransactionMutation.isPending}>
              {t("retail.checkout.counter.serviceSubmit", { defaultValue: "Add service line" })}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>

    <Sheet open={customerLookupOpen} onOpenChange={setCustomerLookupOpen}>
        <SheetContent className="w-full sm:max-w-md">
          <SheetHeader>
            <SheetTitle>{t("retail.checkout.customers.sheetTitle", { defaultValue: "Loyalty customers" })}</SheetTitle>
            <SheetDescription>
              {t("retail.checkout.customers.sheetSubtitle", {
                defaultValue: "Attach a customer to record loyalty credit and send digital receipts.",
              })}
            </SheetDescription>
          </SheetHeader>
          <div className="mt-4 space-y-4">
            <Input
              value={customerSearch}
              onChange={(event) => setCustomerSearch(event.target.value)}
              placeholder={t("retail.checkout.customers.searchPlaceholder", {
                defaultValue: "Search by name, email, or loyalty ID",
              })}
            />
            <ScrollArea className="max-h-[320px] rounded-xl border border-slate-200/70 dark:border-slate-800/60">
              <div className="divide-y divide-slate-200 dark:divide-slate-800">
                {customersQuery.isLoading ? (
                  <div className="space-y-2 p-4">
                    <Skeleton className="h-12 w-full" />
                    <Skeleton className="h-12 w-full" />
                    <Skeleton className="h-12 w-full" />
                  </div>
                ) : customersQuery.isError ? (
                  <div className="px-4 py-6 text-center text-sm text-slate-500 dark:text-slate-400">
                    {t("retail.checkout.customers.loadError", {
                      defaultValue: "We couldn't load customers right now. Please try again.",
                    })}
                  </div>
                ) : customerResults.length === 0 ? (
                  <div className="px-4 py-6 text-center text-sm text-slate-500 dark:text-slate-400">
                    {t("retail.checkout.customers.noResults", { defaultValue: "No customers match this search." })}
                  </div>
                ) : (
                  customerResults.map((customer) => (
                    <button
                      type="button"
                      key={customer.id}
                      className="flex w-full flex-col items-start gap-1 px-4 py-3 text-left transition hover:bg-emerald-50/60 dark:hover:bg-emerald-500/10"
                      onClick={() => handleSelectCustomer(customer)}
                    >
                      <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                        {customer.name ?? t("retail.checkout.customers.unknownName", { defaultValue: "Guest customer" })}
                      </span>
                      {customer.email ? (
                        <span className="text-xs text-slate-500 dark:text-slate-400">{customer.email}</span>
                      ) : null}
                      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                        {customer.loyaltyId ? (
                          <Badge variant="outline" className="uppercase tracking-wide">
                            {t("retail.checkout.customers.loyaltyId", {
                              defaultValue: "Loyalty {{id}}",
                              id: customer.loyaltyId,
                            })}
                          </Badge>
                        ) : null}
                        <span>
                          {t("retail.checkout.customers.spendToDate", {
                            defaultValue: "Spend to date {{amount}}",
                            amount: formatCurrencyFromCents(customer.spendToDateCents),
                          })}
                        </span>
                        <span>
                          {customer.lastVisitAt
                            ? t("retail.checkout.customers.lastSeen", {
                                defaultValue: "Last visit {{time}}",
                                time: formatRelativeTime(customer.lastVisitAt),
                              })
                            : t("retail.checkout.customers.noVisits", {
                                defaultValue: "No recorded visits yet",
                              })}
                        </span>
                        {typeof customer.visitCount === "number" && customer.visitCount > 0 ? (
                          <span>
                            {t("retail.checkout.customers.visitCount", {
                              defaultValue: "{{count}} visits",
                              count: customer.visitCount,
                            })}
                          </span>
                        ) : null}
                      </div>
                    </button>
                  ))
                )}
              </div>
            </ScrollArea>
          </div>
        </SheetContent>
      </Sheet>

      <RetailQuickSaleDialog
        open={quickSaleOpen}
        onOpenChange={(open) => {
          setQuickSaleOpen(open);
          if (!open) {
            setQuickSaleMode("standard");
          }
        }}
        initialMode={quickSaleMode}
      />
    </div>
  );
};

export default RetailCheckoutPage;
